/* eslint-disable max-len */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { ActivityViewsService } from 'app/modules/funnel/models/activity-view.service';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { TranscriptMessage } from 'app/transcript/transcript-message';
import { environment } from 'environments/environment';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { Sms } from '../sms-service/sms.model';
import {
  AddSms,
  ClearSmsStore,
  GetFullBorrowerSmsHistory,
  MarkSmsRead,
  SubscribeToBorrowerTexts
} from '../sms-store/sms.actions';
import { SmsState } from '../sms-store/sms.state';

interface Borrower {
  id: number;
  canText: boolean;
  smsUnsubscribed: boolean;
  status: string;
  primary_contact: {
    first: string;
    last: string;
    mobilePhone: number;
    mobilePhoneCountryCodeId: string;
    mobilePhoneCountryCode: string;
  };
}

@Component({
  selector: 'app-sms-smart',
  templateUrl: './sms-smart.component.html',
  styleUrls: ['./sms-smart.component.scss'],
})
export class SmsSmartComponent implements OnInit, OnDestroy {

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(SmsState.alert) smsAlert$: Observable<null | { message: string, level: string }>;
  @Select(SmsState.sms) messages$: Observable<Sms[]>;
  @Input() borrower: Borrower;
  @Input() showHistory = true;

  transcriptMessages$: Observable<any[]>;
  complianceReview$: Observable<BorrowerValue | null>;
  inComplianceReview = false;
  borrower$: Observable<Borrower>;
  loadingInitial$: Observable<boolean>;
  loadingAll$: Observable<boolean>;
  disableLoadAll: boolean;
  destroyed$ = new Subject<boolean>();

  errorMap = [
    {
      includes: 'is not a valid phone number',
      message: 'Whoops, this message didn\'t go through because this is not a valid phone number. Try verifying and correcting the number through an email with the merchant.'
    },
    {
      includes: '1600',
      message: 'Whoops, this message didn\'t go through because this message is too long. Try breaking it up into messages that are 1600 characters or less.'
    },
    {
      includes: 'phone number is required',
      message: 'Whoops, this message didn\'t go through because there is no phone number correlated with this borrower. Try adding a phone number in the merchant overview page.'
    },
    {
      includes: 'pair violates a blacklist rule',
      message: 'Whoops, this message didn\'t go through because your number has been blacklisted. Try contacting this merchant through email.'
    },
    {
      includes: 'Authenticate',
      message: 'Whoops, this message didn\'t go through because of an unknown error. Try submitting a help desk ticket so that we can solve this problem for future messages.'
    },
    {
      includes: 'is not a mobile number',
      message: 'Whoops, this message didn\'t go through because this is not a valid phone number. Try verifying and correcting the number through an email with the merchant.'
    },
    {
      includes: 'via SMS',
      message: 'Whoops, this message didn\'t go through because this is not a valid phone number. Try verifying and correcting the number through an email with the merchant.'
    },
    {
      includes: 'Operation timed out',
      message: 'Whoops, this message didn\'t go through because of an unknown error. Try submitting a help desk ticket so that we can solve this problem for future messages.'
    },
    {
      includes: 'SMS-capable',
      message: 'Whoops, this message didn\'t go through because this is not a valid phone number. Try verifying and correcting the number through an email with the merchant.'
    },
    {
      includes: 'internal server error',
      message: 'Texting is temporarily disabled, please try again later.'
    },
    {
      includes: 'unsubscribed from receiving text messages',
      message: 'Whoops, this message didn\'t go through because the merchant has unsubscribed from receiving text messages.'
    },

  ];

  constructor(
    private store: Store,
    private activityViewsService:  ActivityViewsService
  ) { }

  ngOnInit() {
    this.store.dispatch(new SubscribeToBorrowerTexts(this.borrower.id));

    this.transcriptMessages$ = this.store.select(state => state.sms.sms.map(this.smsToTranscriptMessage.bind(this)));

    this.messages$.pipe( takeUntil(this.destroyed$) )
      .subscribe( messages => {
        this.markSmsRead(messages);
      });

    this.loadingInitial$ = this.store.select(state => state.sms.loadingInitial);

    this.loadingAll$ = this.store.select(state => state.sms.loadingAll);

    this.activityViewsService.updateOrCreate(this.borrower.id, 'sms').pipe(
      catchError(() => {
        return of();
      })
    ).subscribe();

    this.complianceReview$ = this.store.select(BorrowerValueState.valueByAlias)
      .pipe(map(filterFn => filterFn('complianceReview')));

    this.complianceReview$.subscribe(complianceReview => {
      this.inComplianceReview = complianceReview && complianceReview.value !== null ? true : false;
    });

  }

  ngOnDestroy() {
    this.store.dispatch(new ClearSmsStore(this.borrower.id));
    this.destroyed$.next(false);
  }

  smsToTranscriptMessage(sms: Sms): TranscriptMessage {

    const fromUser = sms.type === 'incoming'
      ? {
        id: sms.borrower.clientId,
        fullName: sms.borrower.primary_contact ? `${sms.borrower.primary_contact.first} ${sms.borrower.primary_contact.last}` : '',
        client: true,
        attachments: sms.attachments
      }
      : {
        id: sms.user.id,
        fullName: sms.user ? `${sms.user.first} ${sms.user.last}` : '',
        client: false
      };

    return {
      content: sms.content,
      fromUser: {
        id: fromUser.id,
        fullName: fromUser.fullName,
        client: fromUser.client
      },
      modifiedTimestamp: sms.modifiedTimestamp,
      attachments: sms.attachments.map(this.documentToTranscriptAttachment),
      status: sms.status,
      errorMessage: sms.errorMessage ? this.mapErrorMessage(sms.errorMessage) : undefined,
      templateId: sms.templateId,
      isAutomated: sms.isAutomated,
      automatedAttemptNumber: sms.automatedAttemptNumber,
      type: sms.type
    };
  }

  documentToTranscriptAttachment(document) {
    return {
        filename: document.filename,
        url: `${environment.API_URL}/document/${document.id}?stream=1`
    };
  }

  handleSubmitSms(smsData): void {
    this.store.dispatch(new AddSms({
      content: smsData.content,
      borrowerId: this.borrower.id,
      toNumber: this.formatPhoneNumber(this.borrower.primary_contact),
      templateId: smsData.templateId,
    }));
  }

  /**
   *
   * @param primaryContact
   * @return string
   */
  formatPhoneNumber(primaryContact: Borrower['primary_contact']): string {
    let phone;
    if (primaryContact?.mobilePhoneCountryCode) {
      phone = `+${primaryContact.mobilePhoneCountryCode}${primaryContact.mobilePhone}`;
    } else {
      phone = primaryContact.mobilePhone.toString();
    }

    return phone;
  }

  mapErrorMessage(errorMessage: string): string {
    let returnedMessage: string | undefined;
    returnedMessage = this.errorMap.find(mapped => errorMessage.includes(mapped.includes))?.message;
    returnedMessage ||= (errorMessage.length > 160 ? errorMessage.substring(0, 157) + '...' : errorMessage);
    return returnedMessage;
  }

  loadAllMessages(): void {
    this.disableLoadAll = true;
    this.store.dispatch(new GetFullBorrowerSmsHistory(this.borrower.id));
  }

  markSmsRead(messages) {

    this.lenderUser$.pipe( take(1) )
      .subscribe( (lenderUser: LenderUser) => {
        // endpoint will mark read all SMS with combo of borrowerId and lenderUserId, no need to send individual IDs
        return messages.length ?
          this.store.dispatch( new MarkSmsRead(lenderUser.id, [], this.borrower.id)) :
          false;
      });
  }
}
